import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiZoomIn, FiZoomOut, FiMove, FiX, FiDownload, FiGrid,
  FiEdit3, FiTrash2, FiPlus, FiCheck, FiMaximize2,
  FiChevronLeft, FiChevronRight, FiLayout, FiLink2,
  FiFileText, FiList, FiShare2, FiLayers, FiCalendar,
  FiClock, FiZap, FiAlignLeft, FiGitMerge,
} from 'react-icons/fi';
import styles from '../styles/documentCanvas.module.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Graph node category colors ───────────────────────────────────────────────
const GRAPH_COLORS = {
  concept:     { bg: '#1e1b4b', border: '#818cf8', text: '#c7d2fe', label: '#818cf8' },
  process:     { bg: '#1e3a5f', border: '#38bdf8', text: '#bae6fd', label: '#38bdf8' },
  entity:      { bg: '#14532d', border: '#34d399', text: '#a7f3d0', label: '#34d399' },
  property:    { bg: '#451a03', border: '#fb923c', text: '#fed7aa', label: '#fb923c' },
  event:       { bg: '#2d1b69', border: '#a78bfa', text: '#ddd6fe', label: '#a78bfa' },
  formula:     { bg: '#4a1942', border: '#e879f9', text: '#f5d0fe', label: '#e879f9' },
  theory:      { bg: '#0c4a6e', border: '#22d3ee', text: '#a5f3fc', label: '#22d3ee' },
  application: { bg: '#052e16', border: '#4ade80', text: '#bbf7d0', label: '#4ade80' },
  default:     { bg: '#1a1a2e', border: '#9333ea', text: '#e9d5ff', label: '#c084fc' },
};

const NOTE_COLORS = [
  { id: 'yellow', bg: '#FFFBEB', border: '#D97706', label: 'Key Idea',  dark: '#78350F' },
  { id: 'pink',   bg: '#FFF0F6', border: '#DB2777', label: 'Important', dark: '#831843' },
  { id: 'blue',   bg: '#EFF6FF', border: '#2563EB', label: 'Notes',     dark: '#1E3A5F' },
  { id: 'green',  bg: '#F0FDF4', border: '#16A34A', label: 'Summary',   dark: '#14532D' },
  { id: 'purple', bg: '#FAF5FF', border: '#9333EA', label: 'Insight',   dark: '#581C87' },
  { id: 'orange', bg: '#FFF7ED', border: '#EA580C', label: 'Action',    dark: '#7C2D12' },
];

// ─── Student Templates ────────────────────────────────────────────────────────
const TEMPLATES = [
  {
    id: 'cornell', name: 'Cornell Notes', Icon: FiFileText,
    desc: 'Structured note-taking with cues & summary',
    color: '#2563EB',
    create: () => ({
      items: [
        { id: 'c1', colorId: 'yellow', text: 'Cue Column\n\nKey terms\nQuestions\nMain ideas', position: { x: 60, y: 80 }, size: { w: 200, h: 280 } },
        { id: 'c2', colorId: 'blue',   text: 'Main Notes\n\nWrite your lecture or reading notes here. Include examples and key points.', position: { x: 280, y: 80 }, size: { w: 380, h: 280 } },
        { id: 'c3', colorId: 'green',  text: 'Summary\n\nWrite a 2-3 sentence summary of the main ideas after your session.', position: { x: 60, y: 380 }, size: { w: 600, h: 110 } },
      ],
      connections: [],
    }),
  },
  {
    id: 'kanban', name: 'Study Board', Icon: FiList,
    desc: 'Track tasks: To Do → Doing → Done',
    color: '#9333EA',
    create: () => ({
      items: [
        { id: 'k1', colorId: 'blue',   text: 'To Do\n\nRead Chapter 5\nReview flashcards\nWatch lecture video\nPractice problems', position: { x: 60,  y: 80 }, size: { w: 220, h: 260 } },
        { id: 'k2', colorId: 'yellow', text: 'In Progress\n\nPractice set 3\nEssay outline\n\nMove tasks here when working', position: { x: 310, y: 80 }, size: { w: 220, h: 260 } },
        { id: 'k3', colorId: 'green',  text: 'Done\n\nQuiz 1\nLab report\n\nMove tasks here when complete', position: { x: 560, y: 80 }, size: { w: 220, h: 260 } },
      ],
      connections: [],
    }),
  },
  {
    id: 'mindmap', name: 'Mind Map', Icon: FiShare2,
    desc: 'Visual brainstorming with connections',
    color: '#D97706',
    create: () => ({
      items: [
        { id: 'm0', colorId: 'purple', text: 'Central Topic\n\nYour main concept here', position: { x: 300, y: 180 }, size: { w: 210, h: 100 } },
        { id: 'm1', colorId: 'yellow', text: 'Branch 1\n\nIdea or subtopic', position: { x: 60,  y: 60  }, size: { w: 190, h: 90 } },
        { id: 'm2', colorId: 'blue',   text: 'Branch 2\n\nIdea or subtopic', position: { x: 560, y: 60  }, size: { w: 190, h: 90 } },
        { id: 'm3', colorId: 'green',  text: 'Branch 3\n\nIdea or subtopic', position: { x: 60,  y: 310 }, size: { w: 190, h: 90 } },
        { id: 'm4', colorId: 'orange', text: 'Branch 4\n\nIdea or subtopic', position: { x: 560, y: 310 }, size: { w: 190, h: 90 } },
        { id: 'm5', colorId: 'pink',   text: 'Branch 5\n\nIdea or subtopic', position: { x: 300, y: 380 }, size: { w: 190, h: 90 } },
      ],
      connections: [
        { id: 'mc1', from: 'm0', to: 'm1', color: '#D97706' },
        { id: 'mc2', from: 'm0', to: 'm2', color: '#2563EB' },
        { id: 'mc3', from: 'm0', to: 'm3', color: '#16A34A' },
        { id: 'mc4', from: 'm0', to: 'm4', color: '#EA580C' },
        { id: 'mc5', from: 'm0', to: 'm5', color: '#DB2777' },
      ],
    }),
  },
  {
    id: 'flashcards', name: 'Flashcards', Icon: FiLayers,
    desc: 'Q & A study pairs side by side',
    color: '#DB2777',
    create: () => ({
      items: [
        { id: 'fa1', colorId: 'blue',  text: 'Question 1\n\nWrite your question here.', position: { x: 60,  y: 80  }, size: { w: 210, h: 130 } },
        { id: 'fa2', colorId: 'green', text: 'Answer 1\n\nWrite your answer here.',     position: { x: 290, y: 80  }, size: { w: 210, h: 130 } },
        { id: 'fb1', colorId: 'blue',  text: 'Question 2\n\nWrite your question here.', position: { x: 60,  y: 230 }, size: { w: 210, h: 130 } },
        { id: 'fb2', colorId: 'green', text: 'Answer 2\n\nWrite your answer here.',     position: { x: 290, y: 230 }, size: { w: 210, h: 130 } },
        { id: 'fc1', colorId: 'blue',  text: 'Question 3\n\nWrite your question here.', position: { x: 60,  y: 380 }, size: { w: 210, h: 130 } },
        { id: 'fc2', colorId: 'green', text: 'Answer 3\n\nWrite your answer here.',     position: { x: 290, y: 380 }, size: { w: 210, h: 130 } },
      ],
      connections: [],
    }),
  },
  {
    id: 'plan', name: 'Study Plan', Icon: FiCalendar,
    desc: 'Plan your week of study sessions',
    color: '#16A34A',
    create: () => ({
      items: [
        { id: 'sp0', colorId: 'purple', text: 'Goals This Week\n\nDeadline:', position: { x: 60,  y: 80  }, size: { w: 220, h: 170 } },
        { id: 'sp1', colorId: 'blue',   text: 'Monday\n\n',    position: { x: 310, y: 80  }, size: { w: 180, h: 150 } },
        { id: 'sp2', colorId: 'blue',   text: 'Tuesday\n\n',   position: { x: 510, y: 80  }, size: { w: 180, h: 150 } },
        { id: 'sp3', colorId: 'yellow', text: 'Wednesday\n\n', position: { x: 310, y: 250 }, size: { w: 180, h: 150 } },
        { id: 'sp4', colorId: 'yellow', text: 'Thursday\n\n',  position: { x: 510, y: 250 }, size: { w: 180, h: 150 } },
        { id: 'sp5', colorId: 'orange', text: 'Weekend Review\n\nReview:\nPractice:', position: { x: 60, y: 270 }, size: { w: 220, h: 150 } },
      ],
      connections: [],
    }),
  },
  {
    id: 'assignment', name: 'Assignment Builder', Icon: FiGitMerge,
    desc: 'AI-assisted assignment outline with requirements & research',
    color: '#c084fc',
    create: () => ({
      items: [
        { id: 'as0', colorId: 'purple', text: '📋 Assignment Title\n\nWrite your assignment title here.\n\nCourse:\nDue Date:\nInstructor:', position: { x: 240, y: 40 }, size: { w: 360, h: 150 } },
        { id: 'as1', colorId: 'blue',   text: '🎯 Requirements\n\nList every requirement:\n• Page count:\n• Citation style:\n• Sources needed:\n• Format:', position: { x: 40,  y: 220 }, size: { w: 230, h: 200 } },
        { id: 'as2', colorId: 'yellow', text: '🔬 Research Plan\n\nKey sources to find:\n1.\n2.\n3.\n\nKeywords to search:', position: { x: 300, y: 220 }, size: { w: 230, h: 200 } },
        { id: 'as3', colorId: 'orange', text: '⏱ Timeline\n\nResearch: ___\nOutline: ___\nFirst draft: ___\nRevisions: ___\nSubmit: ___', position: { x: 560, y: 220 }, size: { w: 220, h: 200 } },
        { id: 'as4', colorId: 'green',  text: '📝 Outline\n\nIntro:\n\nMain Point 1:\n\nMain Point 2:\n\nMain Point 3:\n\nConclusion:', position: { x: 40,  y: 450 }, size: { w: 340, h: 230 } },
        { id: 'as5', colorId: 'pink',   text: '💡 AI Assistance\n\nAsk Lumi to help:\n• "Expand my outline into a draft"\n• "Suggest sources for [topic]"\n• "Check my argument structure"\n• "Summarize my notes into points"\n\nClick Ask Lumi (⚡) on any card!', position: { x: 410, y: 450 }, size: { w: 370, h: 230 } },
      ],
      connections: [
        { id: 'asc1', from: 'as0', to: 'as1', color: '#818cf8' },
        { id: 'asc2', from: 'as0', to: 'as2', color: '#818cf8' },
        { id: 'asc3', from: 'as0', to: 'as3', color: '#818cf8' },
        { id: 'asc4', from: 'as1', to: 'as4', color: '#4ade80' },
        { id: 'asc5', from: 'as2', to: 'as4', color: '#4ade80' },
      ],
    }),
  },
  {
    id: 'essay', name: 'Essay Planner', Icon: FiAlignLeft,
    desc: 'Structure an essay then draft it with Lumi',
    color: '#0891B2',
    create: () => ({
      items: [
        { id: 'es0', colorId: 'blue',   text: 'Thesis Statement\n\nWrite your main argument here. One clear, debatable claim.', position: { x: 280, y: 60  }, size: { w: 300, h: 120 } },
        { id: 'es1', colorId: 'yellow', text: 'Argument 1\n\nFirst supporting point\n\nEvidence:', position: { x: 60,  y: 240 }, size: { w: 220, h: 170 } },
        { id: 'es2', colorId: 'yellow', text: 'Argument 2\n\nSecond supporting point\n\nEvidence:', position: { x: 320, y: 240 }, size: { w: 220, h: 170 } },
        { id: 'es3', colorId: 'yellow', text: 'Argument 3\n\nThird supporting point\n\nEvidence:', position: { x: 580, y: 240 }, size: { w: 220, h: 170 } },
        { id: 'es4', colorId: 'pink',   text: 'Counterargument\n\nOpposing view and your rebuttal:', position: { x: 60,  y: 470 }, size: { w: 220, h: 130 } },
        { id: 'es5', colorId: 'green',  text: 'Conclusion\n\nRestate thesis and summarize. End with broader implication.', position: { x: 320, y: 470 }, size: { w: 480, h: 130 } },
      ],
      connections: [
        { id: 'ec1', from: 'es0', to: 'es1', color: '#D97706' },
        { id: 'ec2', from: 'es0', to: 'es2', color: '#D97706' },
        { id: 'ec3', from: 'es0', to: 'es3', color: '#D97706' },
        { id: 'ec4', from: 'es1', to: 'es5', color: '#16A34A' },
        { id: 'ec5', from: 'es2', to: 'es5', color: '#16A34A' },
        { id: 'ec6', from: 'es3', to: 'es5', color: '#16A34A' },
      ],
    }),
  },
];

// ─── Knowledge graph radial layout helper ────────────────────────────────────
const buildKgItems = (nodes, edges) => {
  if (!nodes || nodes.length === 0) return { items: [], connections: [] };
  const cx = 400, cy = 280;

  // Sort by importance so high-importance nodes are inner ring
  const high   = nodes.filter(n => n.importance === 'high');
  const medium  = nodes.filter(n => n.importance === 'medium');
  const low    = nodes.filter(n => n.importance !== 'high' && n.importance !== 'medium');

  const placed = {};
  const items = [];

  // First node → center
  const center = nodes[0];
  placed[center.id] = { x: cx - 80, y: cy - 40 };
  items.push({
    id: center.id, type: 'knowledge',
    label: center.label, category: center.category,
    description: center.description, importance: 'high',
    position: { x: cx - 80, y: cy - 40 }, size: { w: 160, h: 80 },
    colorKey: center.category || 'default',
  });

  // Place remaining nodes in rings
  const rings = [
    { nodes: high.filter(n => n.id !== center.id),   r: 210, nodeW: 140, nodeH: 60 },
    { nodes: medium,                                  r: 370, nodeW: 120, nodeH: 54 },
    { nodes: low,                                     r: 520, nodeW: 110, nodeH: 50 },
  ];

  rings.forEach(({ nodes: ring, r, nodeW, nodeH }) => {
    if (!ring.length) return;
    ring.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / ring.length - Math.PI / 2;
      const px = cx + r * Math.cos(angle) - nodeW / 2;
      const py = cy + r * Math.sin(angle) - nodeH / 2;
      placed[n.id] = { x: px, y: py };
      items.push({
        id: n.id, type: 'knowledge',
        label: n.label, category: n.category,
        description: n.description, importance: n.importance,
        position: { x: px, y: py }, size: { w: nodeW, h: nodeH },
        colorKey: n.category || 'default',
      });
    });
  });

  const connections = (edges || [])
    .filter(e => placed[e.from] && placed[e.to])
    .map((e, i) => {
      const srcNode = nodes.find(n => n.id === e.from);
      const srcCol = GRAPH_COLORS[srcNode?.category] || GRAPH_COLORS.default;
      return {
        id: `kgc_${i}`, from: e.from, to: e.to,
        label: e.label || '',
        color: srcCol.border,
        strength: e.strength,
      };
    });

  return { items, connections };
};

// ─── Draggable Hook ───────────────────────────────────────────────────────────
const useDraggable = (id, onDrag, canvasZoom, disabled = false) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ start: { x: 0, y: 0 } });

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0 || disabled) return;
    e.stopPropagation();
    setIsDragging(true);
    dragRef.current.start = { x: e.clientX, y: e.clientY };
  }, [disabled]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const dx = (e.clientX - dragRef.current.start.x) / canvasZoom;
    const dy = (e.clientY - dragRef.current.start.y) / canvasZoom;
    dragRef.current.start = { x: e.clientX, y: e.clientY };
    onDrag(id, dx, dy);
  }, [isDragging, canvasZoom, onDrag, id]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return { handleMouseDown, isDragging };
};

// ─── Template Gallery ─────────────────────────────────────────────────────────
const TemplateGallery = ({ onSelect, onDismiss }) => (
  <div className={styles.galleryOverlay}>
    <div className={styles.galleryBox}>
      <div className={styles.galleryHeader}>
        <div>
          <h2 className={styles.galleryTitle}>Start with a template</h2>
          <p className={styles.gallerySubtitle}>Pick a layout to jump-start your session</p>
        </div>
        <button className={styles.galleryDismiss} onClick={onDismiss}><FiX size={12} /> Start blank</button>
      </div>
      <div className={styles.galleryGrid}>
        {TEMPLATES.map(t => (
          <button key={t.id} className={styles.templateCard} onClick={() => onSelect(t)}>
            <div className={styles.templateIconBox} style={{ background: t.color + '15', borderColor: t.color + '35' }}>
              <t.Icon size={20} color={t.color} strokeWidth={1.6} />
            </div>
            <span className={styles.templateName}>{t.name}</span>
            <span className={styles.templateDesc}>{t.desc}</span>
          </button>
        ))}
      </div>
    </div>
  </div>
);

// ─── Shared resize start helper ────────────────────────────────────────────────
const useResizeStart = (item, zoom, onResize, cardRef) =>
  useCallback((e) => {
    e.stopPropagation(); e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const startW = item.size?.w || 260;
    const startH = cardRef.current?.offsetHeight || 180;
    const onMove = (me) => onResize(item.id, {
      w: Math.max(160, startW + (me.clientX - startX) / zoom),
      h: Math.max(90,  startH + (me.clientY - startY) / zoom),
    });
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [item, zoom, onResize, cardRef]);

// ─── Note Card ────────────────────────────────────────────────────────────────
const NoteCard = ({ item, isSelected, isConnectSource, zoom, onDrag, onSelect, onDelete, onUpdate, onColorChange, onResize, connectMode, onAskLumi }) => {
  const [editText, setEditText]     = useState(item.text);
  const [isEditing, setIsEditing]   = useState(item.editing || false);
  const [showColors, setShowColors] = useState(false);
  const textRef = useRef(null);
  const cardRef = useRef(null);
  const { handleMouseDown, isDragging } = useDraggable(item.id, onDrag, zoom, isEditing || connectMode);
  const col = NOTE_COLORS.find(c => c.id === item.colorId) || NOTE_COLORS[0];
  const onResizeStart = useResizeStart(item, zoom, onResize, cardRef);

  useEffect(() => {
    if (isEditing && textRef.current) { textRef.current.focus(); }
  }, [isEditing]);

  const commitEdit = () => { setIsEditing(false); onUpdate(item.id, editText); };
  const w = item.size?.w || 260;
  const h = item.size?.h;

  return (
    <motion.div
      ref={cardRef}
      className={`${styles.noteCard} ${isSelected ? styles.noteSelected : ''} ${isDragging ? styles.dragging : ''} ${isConnectSource ? styles.connectSource : ''}`}
      style={{
        left: item.position.x, top: item.position.y, width: w,
        ...(h ? { height: h } : {}),
        background: col.bg,
        borderColor: isConnectSource ? '#9333EA' : isSelected ? col.border : col.border + '55',
        cursor: connectMode ? 'crosshair' : isDragging ? 'grabbing' : 'default',
      }}
      initial={{ opacity: 0, scale: 0.9, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      <div className={styles.noteHeader} style={{ background: col.border }}
        onMouseDown={(e) => { if (!isEditing && !connectMode) { handleMouseDown(e); onSelect(); } }}
        onDoubleClick={() => !connectMode && setIsEditing(true)}
      >
        <span className={styles.noteLabel}>{col.label}</span>
        <div className={styles.noteActions}>
          {onAskLumi && item.text?.trim() && (
            <button className={styles.noteActionBtn} title="Ask Lumi"
              onClick={(e) => { e.stopPropagation(); onAskLumi(item.text); }}>
              <FiZap size={9} />
            </button>
          )}
          <button className={styles.noteActionBtn} title="Color" onClick={(e) => { e.stopPropagation(); setShowColors(v => !v); }}>
            <span className={styles.colorDotSmall} style={{ background: 'rgba(255,255,255,0.7)' }} />
          </button>
          <button className={styles.noteActionBtn} title="Edit" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}><FiEdit3 size={9} /></button>
          <button className={`${styles.noteActionBtn} ${styles.noteDeleteBtn}`} title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(); }}><FiTrash2 size={9} /></button>
        </div>
      </div>

      <AnimatePresence>
        {showColors && (
          <motion.div className={styles.colorPicker}
            initial={{ opacity: 0, y: -4, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -4, scale: 0.95 }}>
            {NOTE_COLORS.map(c => (
              <button key={c.id} className={styles.colorDot}
                style={{ background: c.border, outline: item.colorId === c.id ? `2px solid ${c.border}` : 'none', outlineOffset: 2 }}
                onClick={(e) => { e.stopPropagation(); onColorChange(item.id, c.id); setShowColors(false); }}
                title={c.label} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className={styles.noteBody} onDoubleClick={() => !connectMode && setIsEditing(true)}>
        {isEditing ? (
          <div className={styles.noteEditWrap}>
            <textarea ref={textRef} className={styles.noteTextarea} value={editText}
              onChange={e => setEditText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') commitEdit(); if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commitEdit(); }}
              placeholder="Type your note…" style={{ background: col.bg, color: col.dark }} />
            <button className={styles.noteCommit} style={{ background: col.border }} onClick={commitEdit}><FiCheck size={11} /> Done</button>
          </div>
        ) : (
          <p className={styles.noteText} style={{ color: col.dark }}>
            {item.text || <span style={{ opacity: 0.32, fontStyle: 'italic' }}>Double-click to edit…</span>}
          </p>
        )}
      </div>

      <div className={styles.resizeHandle} onMouseDown={onResizeStart} title="Drag to resize" />
    </motion.div>
  );
};

// ─── Knowledge Graph Node ─────────────────────────────────────────────────────
const KnowledgeGraphNode = ({ item, isSelected, isConnectSource, zoom, onDrag, onSelect, onDelete, connectMode }) => {
  const [showDesc, setShowDesc] = useState(false);
  const { handleMouseDown, isDragging } = useDraggable(item.id, onDrag, zoom, connectMode);
  const col = GRAPH_COLORS[item.colorKey] || GRAPH_COLORS.default;
  const w = item.size?.w || 130;
  const h = item.size?.h || 56;

  const impSize = item.importance === 'high' ? 14 : item.importance === 'medium' ? 12 : 10.5;

  return (
    <motion.div
      style={{
        position: 'absolute',
        left: item.position.x,
        top: item.position.y,
        width: w,
        minHeight: h,
        borderRadius: '12px',
        border: `1.5px solid ${isSelected || isConnectSource ? col.border : col.border + '60'}`,
        background: col.bg,
        cursor: connectMode ? 'crosshair' : isDragging ? 'grabbing' : 'default',
        boxShadow: isSelected
          ? `0 0 0 2px ${col.border}60, 0 4px 20px ${col.border}30`
          : `0 2px 10px rgba(0,0,0,0.4)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 10px',
        userSelect: 'none',
        zIndex: isSelected ? 10 : 1,
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
      initial={{ opacity: 0, scale: 0.75 }} animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.75 }} transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onMouseDown={(e) => { if (!connectMode) { handleMouseDown(e); onSelect(); } }}
    >
      {/* Category badge */}
      <div style={{
        fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.08em',
        color: col.label, fontWeight: 700, marginBottom: '4px', opacity: 0.8,
      }}>
        {item.category || 'concept'}
      </div>
      {/* Label */}
      <div style={{
        fontSize: `${impSize}px`, fontWeight: 700, color: col.text,
        textAlign: 'center', lineHeight: 1.3,
      }}>
        {item.label}
      </div>
      {/* Description toggle */}
      {item.description && (
        <button
          onClick={(e) => { e.stopPropagation(); setShowDesc(v => !v); }}
          style={{
            marginTop: '5px', background: 'none', border: 'none',
            fontSize: '9px', color: col.label, cursor: 'pointer', opacity: 0.6,
            padding: '1px 4px',
          }}
        >
          {showDesc ? '▲ less' : '▼ more'}
        </button>
      )}
      {showDesc && item.description && (
        <div style={{
          fontSize: '10.5px', color: col.text, opacity: 0.7, lineHeight: 1.4,
          marginTop: '4px', textAlign: 'center', padding: '0 2px',
        }}>
          {item.description}
        </div>
      )}
      {/* Delete button (on select) */}
      {isSelected && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{
            position: 'absolute', top: '-8px', right: '-8px',
            width: '18px', height: '18px', borderRadius: '50%',
            background: '#ef4444', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
          }}
        >
          <FiX size={9} />
        </button>
      )}
    </motion.div>
  );
};

// ─── Checklist Card ───────────────────────────────────────────────────────────
const ChecklistCard = ({ item, isSelected, isConnectSource, zoom, onDrag, onSelect, onDelete, onUpdate, onResize, connectMode, onAskLumi }) => {
  const checks = item.checks || [];
  const [newText, setNewText]         = useState('');
  const [isAddingItem, setIsAddingItem] = useState(checks.length === 0);
  const inputRef = useRef(null);
  const cardRef  = useRef(null);
  const { handleMouseDown, isDragging } = useDraggable(item.id, onDrag, zoom, connectMode);
  const onResizeStart = useResizeStart(item, zoom, onResize, cardRef);

  useEffect(() => {
    if (isAddingItem && inputRef.current) inputRef.current.focus();
  }, [isAddingItem]);

  const pushChecks = (next) => onUpdate(item.id, next);

  const addCheck = () => {
    if (!newText.trim()) return;
    pushChecks([...checks, { id: `chk_${Date.now()}`, text: newText.trim(), checked: false }]);
    setNewText('');
    setIsAddingItem(true);
  };

  const toggleCheck = (id) => pushChecks(checks.map(c => c.id === id ? { ...c, checked: !c.checked } : c));
  const removeCheck = (id) => pushChecks(checks.filter(c => c.id !== id));

  const done = checks.filter(c => c.checked).length;
  const total = checks.length;
  const pct = total > 0 ? (done / total) * 100 : 0;
  const w = item.size?.w || 240;

  const lumiText = checks.map(c => (c.checked ? '[x] ' : '[ ] ') + c.text).join('\n');

  return (
    <motion.div ref={cardRef}
      className={`${styles.noteCard} ${isSelected ? styles.noteSelected : ''} ${isDragging ? styles.dragging : ''} ${isConnectSource ? styles.connectSource : ''}`}
      style={{
        left: item.position.x, top: item.position.y, width: w,
        background: '#F0FDF4',
        borderColor: isConnectSource ? '#9333EA' : isSelected ? '#16A34A' : '#16A34A55',
        cursor: connectMode ? 'crosshair' : 'default',
      }}
      initial={{ opacity: 0, scale: 0.9, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      <div className={styles.noteHeader} style={{ background: '#16A34A' }}
        onMouseDown={(e) => { if (!connectMode) { handleMouseDown(e); onSelect(); } }}>
        <span className={styles.noteLabel}>{total > 0 ? `${done} / ${total} done` : 'Checklist'}</span>
        <div className={styles.noteActions}>
          {onAskLumi && total > 0 && (
            <button className={styles.noteActionBtn} title="Ask Lumi about this list"
              onClick={(e) => { e.stopPropagation(); onAskLumi(lumiText); }}>
              <FiZap size={9} />
            </button>
          )}
          <button className={styles.noteActionBtn} title="Add item"
            onClick={(e) => { e.stopPropagation(); setIsAddingItem(v => !v); }}>
            <FiPlus size={9} />
          </button>
          <button className={`${styles.noteActionBtn} ${styles.noteDeleteBtn}`} title="Delete"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            <FiTrash2 size={9} />
          </button>
        </div>
      </div>

      {total > 0 && (
        <div className={styles.checklistProgress}>
          <motion.div className={styles.checklistBar}
            animate={{ width: `${pct}%` }} transition={{ duration: 0.3, ease: 'easeOut' }} />
        </div>
      )}

      <div className={styles.noteBody}>
        {checks.length === 0 && !isAddingItem && (
          <p style={{ color: '#14532D', opacity: 0.38, fontStyle: 'italic', fontSize: 12, margin: 0 }}>
            Click + to add items
          </p>
        )}
        {checks.map(c => (
          <div key={c.id} className={styles.checkItem}>
            <button className={`${styles.checkBox} ${c.checked ? styles.checkBoxDone : ''}`}
              onClick={(e) => { e.stopPropagation(); toggleCheck(c.id); }}>
              {c.checked && <FiCheck size={9} strokeWidth={3} />}
            </button>
            <span className={styles.checkText}
              style={{ textDecoration: c.checked ? 'line-through' : 'none', opacity: c.checked ? 0.38 : 1 }}>
              {c.text}
            </span>
            <button className={styles.checkDelete}
              onClick={(e) => { e.stopPropagation(); removeCheck(c.id); }}>
              <FiX size={8} />
            </button>
          </div>
        ))}
        {isAddingItem && (
          <div className={styles.checkAddRow}>
            <input ref={inputRef} className={styles.checkInput}
              value={newText} onChange={e => setNewText(e.target.value)}
              placeholder="Add item…"
              onKeyDown={e => {
                if (e.key === 'Enter') addCheck();
                if (e.key === 'Escape') { setIsAddingItem(false); setNewText(''); }
              }} />
            <button className={styles.checkAddBtn} onClick={addCheck}><FiCheck size={10} /></button>
          </div>
        )}
      </div>
      <div className={styles.resizeHandle} onMouseDown={onResizeStart} title="Drag to resize" />
    </motion.div>
  );
};

// ─── Deadline Card ────────────────────────────────────────────────────────────
const DeadlineCard = ({ item, isSelected, isConnectSource, zoom, onDrag, onSelect, onDelete, onUpdate, connectMode }) => {
  const [editing, setEditing] = useState(!item.deadline);
  const [titleVal, setTitleVal] = useState(item.title || '');
  const [dateVal, setDateVal]   = useState(item.deadline || '');
  const cardRef = useRef(null);
  const { handleMouseDown, isDragging } = useDraggable(item.id, onDrag, zoom, connectMode || editing);

  const commit = () => {
    if (!dateVal) return;
    onUpdate(item.id, titleVal, dateVal);
    setEditing(false);
  };

  const daysLeft = item.deadline
    ? Math.ceil((new Date(item.deadline + 'T23:59:59') - Date.now()) / 86400000)
    : null;
  const isOverdue = daysLeft !== null && daysLeft < 0;
  const isToday   = daysLeft === 0;
  const isUrgent  = !isToday && daysLeft !== null && daysLeft <= 2;
  const isWarning = daysLeft !== null && daysLeft > 2 && daysLeft <= 5;
  const accent = isOverdue ? '#DC2626' : (isToday || isUrgent) ? '#EA580C' : isWarning ? '#D97706' : '#2563EB';

  const formatted = item.deadline
    ? new Date(item.deadline + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : '';

  return (
    <motion.div ref={cardRef}
      className={`${styles.noteCard} ${isSelected ? styles.noteSelected : ''} ${isDragging ? styles.dragging : ''} ${isConnectSource ? styles.connectSource : ''}`}
      style={{
        left: item.position.x, top: item.position.y, width: item.size?.w || 220,
        background: accent + '0D',
        borderColor: isConnectSource ? '#9333EA' : isSelected ? accent : accent + '55',
        cursor: connectMode ? 'crosshair' : 'default',
      }}
      initial={{ opacity: 0, scale: 0.9, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      <div className={styles.noteHeader} style={{ background: accent }}
        onMouseDown={(e) => { if (!connectMode && !editing) { handleMouseDown(e); onSelect(); } }}>
        <span className={styles.noteLabel}><FiClock size={9} style={{ marginRight: 4 }} />Deadline</span>
        <div className={styles.noteActions}>
          <button className={styles.noteActionBtn} title="Edit deadline"
            onClick={(e) => { e.stopPropagation(); setTitleVal(item.title || ''); setDateVal(item.deadline || ''); setEditing(true); }}>
            <FiEdit3 size={9} />
          </button>
          <button className={`${styles.noteActionBtn} ${styles.noteDeleteBtn}`} title="Delete"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            <FiTrash2 size={9} />
          </button>
        </div>
      </div>

      <div className={styles.noteBody}>
        {editing ? (
          <div className={styles.deadlineEditWrap}>
            <input className={styles.deadlineInput} placeholder="Assignment name…"
              value={titleVal} onChange={e => setTitleVal(e.target.value)}
              style={{ borderColor: accent + '60' }} />
            <input className={styles.deadlineInput} type="date"
              value={dateVal} onChange={e => setDateVal(e.target.value)}
              style={{ borderColor: accent + '60' }}
              onKeyDown={e => e.key === 'Enter' && commit()} />
            <button className={styles.noteCommit} style={{ background: accent }} onClick={commit}>
              <FiCheck size={11} /> Save
            </button>
          </div>
        ) : (
          <div className={styles.deadlineView}>
            <p className={styles.deadlineTitle} style={{ color: accent }}>
              {item.title || 'Untitled'}
            </p>
            <div className={styles.deadlineCountdown} style={{ color: accent }}>
              {isOverdue
                ? `${Math.abs(daysLeft)}d overdue`
                : isToday ? 'Due today!'
                : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
            </div>
            <p className={styles.deadlineDate}>{formatted}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const API_BASE_CANVAS = API_BASE;

const AI_GEN_TYPES = [
  { id: 'mindmap',    label: 'Mind Map',    icon: FiShare2 },
  { id: 'flashcards', label: 'Flashcards',  icon: FiLayers },
  { id: 'notes',      label: 'Study Notes', icon: FiFileText },
  { id: 'quiz',       label: 'Quiz Layout', icon: FiZap },
  { id: 'study_plan', label: 'Study Plan',  icon: FiCalendar },
];

// ─── Left Panel ───────────────────────────────────────────────────────────────
const LeftPanel = ({ open, onToggle, onAddNote, onAddChecklist, onAddDeadline, onLoadTemplate, onAiGenerate }) => {
  const [aiTopic, setAiTopic]   = useState('');
  const [aiType,  setAiType]    = useState('mindmap');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError,   setAiError]   = useState('');

  const handleGenerate = async () => {
    if (!aiTopic.trim()) return;
    setAiLoading(true);
    setAiError('');
    try {
      const res = await fetch(`${API_BASE_CANVAS}/api/canvas_generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: aiTopic.trim(), type: aiType }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server error ${res.status}`);
      }
      const data = await res.json();
      onAiGenerate(data);
      setAiTopic('');
    } catch (e) {
      setAiError(e.message || 'Generation failed. Make sure the backend is running.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className={`${styles.leftPanel} ${open ? styles.leftPanelOpen : styles.leftPanelClosed}`}>
      <button className={styles.leftPanelToggle} onClick={onToggle} title={open ? 'Collapse' : 'Expand'}>
        {open ? <FiChevronLeft size={13} /> : <FiChevronRight size={13} />}
      </button>
      {open && (
        <div className={styles.leftPanelContent}>

          {/* AI Generation */}
          <div className={styles.lpSection}>
            <div className={styles.lpSectionTitle}><FiZap size={11} /> Generate with AI</div>
            <input
              className={styles.lpAiInput}
              placeholder="Topic, e.g. Photosynthesis…"
              value={aiTopic}
              onChange={e => setAiTopic(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGenerate()}
              disabled={aiLoading}
            />
            <div className={styles.lpAiTypeRow}>
              {AI_GEN_TYPES.map(t => (
                <button
                  key={t.id}
                  className={`${styles.lpAiTypeBtn} ${aiType === t.id ? styles.lpAiTypeBtnActive : ''}`}
                  onClick={() => setAiType(t.id)}
                  title={t.label}
                >
                  <t.icon size={11} strokeWidth={1.8} />
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
            <button
              className={styles.lpAiGenerateBtn}
              onClick={handleGenerate}
              disabled={aiLoading || !aiTopic.trim()}
            >
              {aiLoading ? 'Generating…' : 'Generate Canvas'}
            </button>
            {aiError && <p className={styles.lpAiError}>{aiError}</p>}
          </div>

          {/* Templates */}
          <div className={styles.lpSection}>
            <div className={styles.lpSectionTitle}><FiLayout size={11} /> Templates</div>
            {TEMPLATES.map(t => (
              <button key={t.id} className={styles.lpTemplateBtn} onClick={() => onLoadTemplate(t)} title={t.desc}>
                <t.Icon size={13} color={t.color} strokeWidth={1.8} />
                <span className={styles.lpTemplateName}>{t.name}</span>
              </button>
            ))}
          </div>

          {/* Add Card */}
          <div className={styles.lpSection}>
            <div className={styles.lpSectionTitle}><FiPlus size={11} /> Add Card</div>
            {NOTE_COLORS.map(col => (
              <button key={col.id} className={styles.lpColorBtn} onClick={() => onAddNote(col.id)} title={col.label}>
                <span className={styles.lpColorDot} style={{ background: col.border }} /><span>{col.label}</span>
              </button>
            ))}
            <button className={styles.lpColorBtn} onClick={onAddChecklist} title="Checklist card">
              <span className={styles.lpColorDot} style={{ background: '#16A34A' }} /><span>Checklist</span>
            </button>
            <button className={styles.lpColorBtn} onClick={onAddDeadline} title="Deadline countdown card">
              <span className={styles.lpColorDot} style={{ background: '#2563EB' }} /><span>Deadline</span>
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

// ─── Storage key ──────────────────────────────────────────────────────────────
const STORAGE_KEY = 'bramlo_canvas_v1';

const loadSaved = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};

// ─── Document Canvas (main) ─────────────────────────────────────────────────
const DocumentCanvas = ({ onClose, panelMode = false, onAskLumi, pendingKgText, pendingKgTitle, onKgConsumed }) => {
  const saved = loadSaved();
  const [canvasPos, setCanvasPos]     = useState(saved.canvasPos || { x: 80, y: 60 });
  const [zoom, setZoom]               = useState(saved.zoom || 1);
  const [isPanning, setIsPanning]     = useState(false);
  const [panStart, setPanStart]       = useState({ x: 0, y: 0 });
  const [activeTool, setActiveTool]   = useState('select');
  const [items, setItems]             = useState(saved.items || []);
  const [connections, setConnections] = useState(saved.connections || []);
  const [selectedId, setSelectedId]   = useState(null);
  const [leftOpen, setLeftOpen]       = useState(true);
  const [showGallery, setShowGallery] = useState((saved.items || []).length === 0);
  const [connectMode, setConnectMode] = useState(false);
  const [connectFrom, setConnectFrom] = useState(null);

  const [kgLoading, setKgLoading] = useState(false);

  const canvasRef = useRef(null);
  const isEmpty = items.length === 0;

  // ── Persist to localStorage (debounced) ──────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, connections, canvasPos, zoom }));
    }, 500);
    return () => clearTimeout(t);
  }, [items, connections, canvasPos, zoom]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const centerPos = useCallback(() => {
    const w = canvasRef.current?.offsetWidth || 800;
    const h = canvasRef.current?.offsetHeight || 600;
    return {
      x: (-canvasPos.x / zoom) + w / zoom / 2 - 130 + (Math.random() - 0.5) * 60,
      y: (-canvasPos.y / zoom) + h / zoom / 2 - 80  + (Math.random() - 0.5) * 40,
    };
  }, [canvasPos, zoom]);

  const loadTemplate = useCallback((template) => {
    const result = template.create();
    setItems(result.items.map(it => ({ ...it, editing: false })));
    setConnections((result.connections || []).map((c, i) => ({ ...c, id: c.id || `conn_${Date.now()}_${i}` })));
    setShowGallery(false);
    setCanvasPos({ x: 80, y: 60 });
    setZoom(1);
  }, []);

  const deleteItem = useCallback((id) => {
    setItems(is => is.filter(it => it.id !== id));
    setConnections(cs => cs.filter(c => c.from !== id && c.to !== id));
    setSelectedId(sid => sid === id ? null : sid);
    setConnectFrom(cf => cf === id ? null : cf);
  }, []);

  const addNote = useCallback((colorId = 'yellow') => {
    setItems(is => [...is, { id: `note_${Date.now()}`, type: 'note', colorId, text: '', position: centerPos(), size: { w: 260 }, editing: true }]);
    setShowGallery(false);
  }, [centerPos]);

  const addChecklist = useCallback(() => {
    setItems(is => [...is, { id: `chk_${Date.now()}`, type: 'checklist', checks: [], position: centerPos(), size: { w: 240 } }]);
    setShowGallery(false);
  }, [centerPos]);

  const addDeadline = useCallback(() => {
    setItems(is => [...is, { id: `dl_${Date.now()}`, type: 'deadline', title: '', deadline: '', position: centerPos(), size: { w: 220 } }]);
    setShowGallery(false);
  }, [centerPos]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const inText = document.querySelector('textarea:focus, input:focus');
      if (e.key === 'Escape') {
        if (connectMode) { setConnectMode(false); setConnectFrom(null); return; }
        if (!inText) onClose?.();
      }
      if (inText) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) deleteItem(selectedId);
      if (e.key === 'v' || e.key === 'V') { setActiveTool('select'); setConnectMode(false); }
      if (e.key === 'h' || e.key === 'H') { setActiveTool('pan'); setConnectMode(false); }
      if (e.key === 'n' || e.key === 'N') addNote();
      if (e.key === 'c' || e.key === 'C') setConnectMode(v => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, connectMode, selectedId]);

  // ── Auto knowledge graph from pending text (from notebook section) ─────────
  useEffect(() => {
    if (!pendingKgText) return;
    let cancelled = false;
    setKgLoading(true);
    fetch(`${API_BASE}/api/knowledge_graph`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: pendingKgText, title: pendingKgTitle || 'Knowledge Graph', depth: 'deep' }),
    })
      .then(r => r.json())
      .then(data => {
        if (cancelled || !data.nodes) return;
        const { items: kgItems, connections: kgConns } = buildKgItems(data.nodes, data.edges);
        const cx = 80, cy = 60;
        const offsetItems = kgItems.map(it => ({
          ...it,
          position: { x: it.position.x + cx, y: it.position.y + cy },
        }));
        setItems(offsetItems);
        setConnections(kgConns);
        setShowGallery(false);
        setZoom(0.75);
        setCanvasPos({ x: 80, y: 60 });
        onKgConsumed?.();
      })
      .catch(() => { if (!cancelled) onKgConsumed?.(); })
      .finally(() => { if (!cancelled) setKgLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKgText, pendingKgTitle]);

  // ── Canvas pan / zoom ─────────────────────────────────────────────────────
  const onCanvasDown = useCallback((e) => {
    if (activeTool === 'pan' || e.button === 1 || (e.button === 0 && e.altKey)) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - canvasPos.x, y: e.clientY - canvasPos.y });
      e.preventDefault();
    }
  }, [activeTool, canvasPos]);

  const onCanvasMove = useCallback((e) => {
    if (isPanning) setCanvasPos({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  }, [isPanning, panStart]);

  const onCanvasUp = useCallback(() => setIsPanning(false), []);

  const onWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setZoom(z => Math.min(Math.max(z * (e.deltaY < 0 ? 1.1 : 0.9), 0.15), 4));
    }
  }, []);

  const onItemDrag = useCallback((id, dx, dy) => {
    setItems(is => is.map(it => it.id === id ? { ...it, position: { x: it.position.x + dx, y: it.position.y + dy } } : it));
  }, []);

  // ── Connect mode ──────────────────────────────────────────────────────────
  const handleCardSelect = useCallback((id) => {
    if (connectMode) {
      if (!connectFrom) {
        setConnectFrom(id);
      } else if (connectFrom !== id) {
        const exists = connections.some(c =>
          (c.from === connectFrom && c.to === id) || (c.from === id && c.to === connectFrom));
        if (!exists) {
          const srcColor = NOTE_COLORS.find(c => c.id === items.find(it => it.id === connectFrom)?.colorId)?.border || '#8B5CF6';
          setConnections(cs => [...cs, { id: `conn_${Date.now()}`, from: connectFrom, to: id, color: srcColor }]);
        }
        setConnectFrom(null);
      }
    } else {
      setSelectedId(id);
    }
  }, [connectMode, connectFrom, connections, items]);

  const itemCenter = (id) => {
    const it = items.find(x => x.id === id);
    return it ? { x: it.position.x + (it.size?.w || 260) / 2, y: it.position.y + (it.size?.h ? it.size.h / 2 : 50) } : null;
  };

  // ── Shared update callbacks ───────────────────────────────────────────────
  const onUpdateNote     = useCallback((id, text)    => setItems(is => is.map(it => it.id === id ? { ...it, text, editing: false } : it)), []);
  const onUpdateChecks   = useCallback((id, checks)  => setItems(is => is.map(it => it.id === id ? { ...it, checks } : it)), []);
  const onUpdateDeadline = useCallback((id, title, deadline) => setItems(is => is.map(it => it.id === id ? { ...it, title, deadline } : it)), []);
  const onResize         = useCallback((id, size)    => setItems(is => is.map(it => it.id === id ? { ...it, size } : it)), []);
  const onColorChange    = useCallback((id, colorId) => setItems(is => is.map(it => it.id === id ? { ...it, colorId } : it)), []);

  // ── AI canvas generation ──────────────────────────────────────────────────
  const handleAiGenerate = useCallback((data) => {
    if (!data?.items?.length) return;
    // Offset generated items to canvas center so they don't land at 0,0
    const cx = (-canvasPos.x / zoom) + (canvasRef.current?.offsetWidth || 800) / zoom / 2 - 300;
    const cy = (-canvasPos.y / zoom) + (canvasRef.current?.offsetHeight || 600) / zoom / 2 - 200;
    const offsetItems = data.items.map(it => ({
      ...it,
      editing: false,
      position: { x: it.position.x + cx, y: it.position.y + cy },
    }));
    const offsetConns = (data.connections || []).map(c => ({ ...c }));
    setItems(is => [...is, ...offsetItems]);
    setConnections(cs => [...cs, ...offsetConns]);
    setShowGallery(false);
  }, [canvasPos, zoom]);

  // ── Essay draft ───────────────────────────────────────────────────────────
  const draftEssayWithLumi = useCallback(() => {
    if (!onAskLumi) return;
    const order = ['Thesis Statement', 'Argument 1', 'Argument 2', 'Argument 3', 'Counterargument', 'Conclusion'];
    const noteParts = items
      .filter(it => it.text?.trim())
      .sort((a, b) => {
        const ai = order.findIndex(k => a.text.startsWith(k));
        const bi = order.findIndex(k => b.text.startsWith(k));
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      })
      .map(it => it.text.trim())
      .join('\n\n---\n\n');
    onAskLumi(`Using this essay outline, write a complete academic essay with an introduction, body paragraphs, and conclusion. Follow the structure closely:\n\n${noteParts}`);
  }, [items, onAskLumi]);

  // ── Export ────────────────────────────────────────────────────────────────
  const exportNotes = () => {
    const lines = items.map((it, i) => {
      if (it.type === 'checklist') {
        return `[Checklist ${i + 1}]\n${(it.checks || []).map(c => (c.checked ? '[x] ' : '[ ] ') + c.text).join('\n')}\n`;
      }
      if (it.type === 'deadline') return `[Deadline] ${it.title} — due ${it.deadline}\n`;
      const col = NOTE_COLORS.find(c => c.id === it.colorId);
      return `[${col?.label || 'Note'} ${i + 1}]\n${it.text}\n`;
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'study-workspace.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Unified card renderer ─────────────────────────────────────────────────
  const renderCard = (item) => {
    const shared = {
      key: item.id, item, zoom,
      isSelected: selectedId === item.id,
      isConnectSource: connectFrom === item.id,
      connectMode,
      onDrag: onItemDrag,
      onSelect: () => handleCardSelect(item.id),
      onDelete: () => deleteItem(item.id),
      onResize,
    };
    if (item.type === 'checklist') return <ChecklistCard {...shared} onUpdate={onUpdateChecks} onAskLumi={onAskLumi} />;
    if (item.type === 'deadline')  return <DeadlineCard  {...shared} onUpdate={onUpdateDeadline} />;
    if (item.type === 'knowledge') return <KnowledgeGraphNode {...shared} />;
    return <NoteCard {...shared} onUpdate={onUpdateNote} onColorChange={onColorChange} onAskLumi={onAskLumi} />;
  };

  const isEssayCanvas = items.some(it => it.text?.startsWith('Thesis Statement'));
  const isKgCanvas = items.some(it => it.type === 'knowledge');
  const kgNodeCount = items.filter(it => it.type === 'knowledge').length;

  return (
    <motion.div
      className={`${styles.canvasRoot} ${panelMode ? styles.canvasPanel : ''}`}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      {/* ── Top Bar ── */}
      <div className={styles.topBar}>
        <div className={styles.barSection}>
          <span className={styles.workspaceTitle}>
            <span className={styles.workspaceDot} style={connectMode ? { background: '#9333EA' } : isKgCanvas ? { background: '#818cf8' } : {}} />
            {connectMode ? 'Connect Mode' : isKgCanvas ? 'Knowledge Graph' : 'Study Workspace'}
          </span>
          {!isEmpty && (
            <span className={styles.itemCount}>
              {isKgCanvas ? `${kgNodeCount} nodes · ${connections.length} links` : `${items.length} card${items.length !== 1 ? 's' : ''}`}
            </span>
          )}
        </div>

        <div className={`${styles.barSection} ${styles.barCentre}`}>
          <div className={styles.toolPill}>
            <button className={`${styles.toolBtn} ${activeTool === 'select' && !connectMode ? styles.toolBtnActive : ''}`}
              onClick={() => { setActiveTool('select'); setConnectMode(false); setConnectFrom(null); }} title="Select (V)"><FiMove /></button>
            <button className={`${styles.toolBtn} ${activeTool === 'pan' ? styles.toolBtnActive : ''}`}
              onClick={() => { setActiveTool('pan'); setConnectMode(false); setConnectFrom(null); }} title="Pan (H)"><FiMaximize2 /></button>
            <button className={`${styles.toolBtn} ${connectMode ? styles.toolBtnConnect : ''}`}
              onClick={() => { setConnectMode(v => !v); setConnectFrom(null); if (!connectMode) setActiveTool('select'); }}
              title="Connect cards (C)">
              <FiLink2 />
            </button>
            <div className={styles.toolDivider} />
            <button className={styles.toolBtn} onClick={() => setZoom(z => Math.max(z / 1.2, 0.15))} title="Zoom out"><FiZoomOut /></button>
            <span className={styles.zoomBadge}>{Math.round(zoom * 100)}%</span>
            <button className={styles.toolBtn} onClick={() => setZoom(z => Math.min(z * 1.2, 4))} title="Zoom in"><FiZoomIn /></button>
            <button className={styles.toolBtn} onClick={() => { setZoom(1); setCanvasPos({ x: 80, y: 60 }); }} title="Reset view"><FiGrid /></button>
          </div>
        </div>

        <div className={`${styles.barSection} ${styles.barRight}`}>
          {isEssayCanvas && onAskLumi && (
            <button className={styles.draftBtn} onClick={draftEssayWithLumi} title="Send essay outline to Lumi">
              <FiZap size={12} /> Draft with Lumi
            </button>
          )}
          {!isEmpty && (
            <>
              <button className={styles.toolBtn} onClick={() => setShowGallery(true)} title="Templates"><FiLayout /></button>
              <button className={styles.toolBtn} onClick={exportNotes} title="Export notes"><FiDownload /></button>
              <button className={styles.toolBtn} title="Clear canvas"
                onClick={() => { if (window.confirm('Clear the whole canvas?')) { setItems([]); setConnections([]); setShowGallery(true); } }}>
                <FiTrash2 />
              </button>
            </>
          )}
          <button className={styles.closeBtn} onClick={onClose} title="Close workspace"><FiX /></button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className={styles.canvasBody}>
        <LeftPanel open={leftOpen} onToggle={() => setLeftOpen(v => !v)}
          onAddNote={addNote} onAddChecklist={addChecklist} onAddDeadline={addDeadline} onLoadTemplate={loadTemplate}
          onAiGenerate={handleAiGenerate} />
        <div
          ref={canvasRef}
          className={styles.canvas}
          onMouseDown={onCanvasDown}
          onMouseMove={onCanvasMove}
          onMouseUp={onCanvasUp}
          onMouseLeave={onCanvasUp}
          onWheel={onWheel}
          style={{ cursor: isPanning ? 'grabbing' : activeTool === 'pan' ? 'grab' : connectMode ? 'crosshair' : 'default' }}
          onClick={() => { setSelectedId(null); if (connectMode) setConnectFrom(null); }}
        >
          {kgLoading && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 60,
              background: 'rgba(8,5,18,0.85)', backdropFilter: 'blur(12px)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px',
            }}>
              <div style={{ position: 'relative', width: '64px', height: '64px' }}>
                <svg width="64" height="64" style={{ animation: 'spin 2s linear infinite' }}>
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#818cf8" strokeWidth="2" strokeDasharray="44 88" strokeLinecap="round" />
                </svg>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>🕸️</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#c7d2fe' }}>Building Knowledge Graph</p>
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>Mapping concepts and relationships from your notes…</p>
              </div>
            </div>
          )}
          <AnimatePresence>
            {showGallery && isEmpty && (
              <motion.div style={{ position: 'absolute', inset: 0, zIndex: 50 }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <TemplateGallery onSelect={loadTemplate} onDismiss={() => setShowGallery(false)} />
              </motion.div>
            )}
          </AnimatePresence>

          {!showGallery && isEmpty && (
            <div className={styles.emptyPrompt}>
              <p>Your canvas is empty</p>
              <div className={styles.emptyActions}>
                <button className={styles.emptyBtn} onClick={() => setShowGallery(true)}><FiLayout size={13} /> Browse templates</button>
                <button className={styles.emptyBtn} onClick={() => addNote('yellow')}><FiPlus size={13} /> Blank card</button>
              </div>
            </div>
          )}

          <div className={styles.canvasWorld} style={{ transform: `translate(${canvasPos.x}px,${canvasPos.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
            <svg className={styles.svgLayer}>
              <defs>
                <marker id="arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L0,6 L8,3 z" fill="rgba(139,92,246,0.55)" />
                </marker>
              </defs>
              {connections.map(c => {
                const a = itemCenter(c.from), b = itemCenter(c.to);
                if (!a || !b) return null;
                const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2 - 60;
                const isKg = c.id?.startsWith('kgc_');
                const strokeColor = c.color || '#8B5CF6';
                return (
                  <g key={c.id} style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); setConnections(cs => cs.filter(x => x.id !== c.id)); }}>
                    <path d={`M${a.x},${a.y} Q${mx},${my} ${b.x},${b.y}`} fill="none" stroke="transparent" strokeWidth={14} />
                    <path d={`M${a.x},${a.y} Q${mx},${my} ${b.x},${b.y}`}
                      fill="none" stroke={strokeColor} strokeWidth={isKg ? "1.4" : "1.8"}
                      strokeDasharray={isKg ? "none" : "6,4"}
                      markerEnd="url(#arr)" opacity={isKg ? 0.55 : 0.5} />
                    {c.label && isKg && (
                      <text
                        x={mx} y={my - 4}
                        textAnchor="middle"
                        fontSize="9"
                        fill={strokeColor}
                        opacity="0.75"
                        fontFamily="system-ui, sans-serif"
                        style={{ pointerEvents: 'none' }}
                      >
                        {c.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
            <AnimatePresence>
              {items.map(item => renderCard(item))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {!isEmpty && !connectMode && (
        <button className={styles.fabBtn} onClick={() => addNote()} title="Add card (N)"><FiPlus size={20} /></button>
      )}

      {connectMode && (
        <div className={styles.connectHint}>
          {connectFrom ? 'Now click another card to connect — or click canvas to cancel' : 'Click a card to start a connection'}
        </div>
      )}

      <div className={styles.kbHint}>
        <span><kbd>N</kbd> Card</span>
        <span><kbd>C</kbd> Connect</span>
        <span><kbd>V</kbd> Select</span>
        <span><kbd>H</kbd> Pan</span>
        <span><kbd>Del</kbd> Delete</span>
        <span><kbd>Esc</kbd> Close</span>
      </div>
    </motion.div>
  );
};

export default DocumentCanvas;
